import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MessageCircle, Search, Loader2, ArrowLeft, Home, ShieldCheck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ChatWindow from '../components/ChatWindow';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API = '/api';

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function MessagesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeConv, setActiveConv] = useState(null);

  const convIdParam = searchParams.get('conv');

  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/chat/conversations`, { credentials: 'same-origin' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to load conversations');
        return;
      }
      const data = await res.json();
      const convs = data.conversations || [];
      setConversations(convs);
      // If a conv_id is in URL, set active
      if (convIdParam) {
        const found = convs.find(c => c.conversation_id === convIdParam);
        if (found) setActiveConv(found);
      }
    } catch (err) {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convIdParam]);

  useEffect(() => {
    if (!user) return;
    fetchConversations();
    // Refresh conversation list every 5s to pick up new conversations + unread counts
    const interval = setInterval(fetchConversations, 5000);
    return () => clearInterval(interval);
  }, [user, fetchConversations]);

  const handleSelectConv = (conv) => {
    setActiveConv(conv);
    setSearchParams({ conv: conv.conversation_id });
    // Optimistically clear unread for this conv
    setConversations(prev => prev.map(c => {
      if (c.conversation_id !== conv.conversation_id) return c;
      const isOwner = user?.user_id === c.owner_id;
      return { ...c, [isOwner ? 'unread_owner' : 'unread_tenant']: 0 };
    }));
  };

  const handleBack = () => {
    setActiveConv(null);
    setSearchParams({});
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-slate-500 mb-4">Please log in to access your messages.</p>
            <Link to="/login" className="bg-blue-700 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-800 transition-colors">Login</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!user.is_phone_verified) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-sm mx-auto px-4">
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Mobile Verification Required</h2>
            <p className="text-slate-500 text-sm mb-6">You must verify your mobile number to access Chat. This ensures a safe experience for everyone.</p>
            <Link to="/verify-mobile" className="bg-blue-700 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-800 transition-colors inline-block">Verify Mobile Now</Link>
          </div>
        </div>
      </div>
    );
  }

  const filtered = conversations.filter(c => {
    const q = search.toLowerCase();
    return !q || c.property_title?.toLowerCase().includes(q) || c.tenant_name?.toLowerCase().includes(q) || c.owner_name?.toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="messages-page">
      <Helmet>
        <title>Messages — Rent Mitra</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Navbar />

      <div className="flex-1 flex flex-col max-w-6xl mx-auto w-full px-0 sm:px-4 py-0 sm:py-6" style={{ height: 'calc(100vh - 64px)' }}>
        <div className="flex flex-1 overflow-hidden bg-white sm:rounded-3xl sm:border sm:border-slate-200 sm:shadow-[0_4px_24px_rgba(0,0,0,0.06)]">

          {/* ── Left: Conversation List ── */}
          <div className={`w-full lg:w-80 xl:w-96 flex-shrink-0 border-r border-slate-100 flex flex-col ${activeConv ? 'hidden lg:flex' : 'flex'}`} data-testid="conversation-list">
            {/* Header */}
            <div className="px-4 pt-4 pb-3 border-b border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Messages</h1>
                <span className="text-xs text-slate-400 font-medium">{conversations.length} conversation{conversations.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search conversations…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
                  data-testid="conversation-search"
                />
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                  <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center mb-3">
                    <MessageCircle className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-slate-600 font-semibold text-sm">No conversations yet</p>
                  <p className="text-slate-400 text-xs mt-1">Find a property and click "Chat with Owner" to start.</p>
                  <Link to="/search" className="mt-4 text-blue-700 text-xs font-semibold hover:underline">Browse Properties</Link>
                </div>
              ) : (
                filtered.map(conv => {
                  const isOwner = user?.user_id === conv.owner_id;
                  const otherName = isOwner ? conv.tenant_name : conv.owner_name;
                  const unread = isOwner ? conv.unread_owner : conv.unread_tenant;
                  const isActive = activeConv?.conversation_id === conv.conversation_id;
                  return (
                    <button
                      key={conv.conversation_id}
                      onClick={() => handleSelectConv(conv)}
                      className={`w-full flex items-start gap-3 px-4 py-3.5 transition-colors text-left border-b border-slate-50 ${
                        isActive ? 'bg-blue-50 border-l-2 border-l-blue-600' : 'hover:bg-slate-50'
                      }`}
                      data-testid={`conv-item-${conv.conversation_id}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-blue-700 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {otherName?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <p className={`text-sm font-semibold truncate ${unread > 0 ? 'text-slate-900' : 'text-slate-700'}`}>{otherName}</p>
                          <span className="text-xs text-slate-400 flex-shrink-0 ml-2">{timeAgo(conv.last_message_at)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Home className="w-3 h-3 text-slate-400 flex-shrink-0" />
                          <p className="text-xs text-slate-500 truncate">{conv.property_title}</p>
                        </div>
                        <p className={`text-xs mt-0.5 truncate ${unread > 0 ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>
                          {conv.last_message || 'No messages yet'}
                        </p>
                      </div>
                      {unread > 0 && (
                        <span className="w-5 h-5 bg-blue-700 text-white rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                          {unread > 9 ? '9+' : unread}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* ── Right: Chat Window ── */}
          <div className={`flex-1 flex flex-col ${activeConv ? 'flex' : 'hidden lg:flex'}`} data-testid="chat-panel">
            {activeConv ? (
              <ChatWindow conversation={activeConv} onBack={handleBack} />
            ) : (
              <div className="flex flex-col items-center justify-center flex-1 text-center px-6">
                <div className="w-16 h-16 bg-blue-50 rounded-3xl flex items-center justify-center mb-4">
                  <MessageCircle className="w-8 h-8 text-blue-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-800 mb-1">Your Messages</h2>
                <p className="text-slate-400 text-sm max-w-xs">Select a conversation from the left to start chatting. All contact details are protected.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
